import React, { useEffect, useState, useRef } from 'react';
import { Instagram } from 'lucide-react';

/**
 * Eine einfache Implementierung des Reveal-on-Scroll Hooks,
 * um externe Abhängigkeiten zu vermeiden.
 */
const useRevealOnScroll = (options = {
  threshold: 0.1
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, options);
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [options]);
  return {
    ref,
    isVisible
  };
};

/**
 * Interne Link-/Button-Komponente:
 * - rendert als BUTTON
 * - kann interne und externe Ziele öffnen
 * - unterstützt newTab
 */
const CustomLink = ({
  to,
  children,
  className,
  newTab,
  ...rest
}) => {
  const handleClick = () => {
    if (!to) return;

    // Externe Links (http/https/www) erkennen
    const isExternal = to.startsWith('http://') || to.startsWith('https://') || to.startsWith('www.');
    const finalUrl = isExternal ? to.startsWith('http') ? to : `https://${to}` : to;
    if (newTab) {
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = finalUrl;
    }
  };
  return <button type="button" onClick={handleClick} className={className} {...rest}>
      {children}
    </button>;
};
export default function App() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <footer ref={ref} className="relative w-full py-12 transition-all duration-1000 opacity-100 translate-y-0 !bg-[hsl(90,61%,26%)]">
      {/* Container matching the Header max-width and padding */}
      <div className="max-w-[1600px] mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-8">

          {/* Logo / Brand - Exakt am linken Rand wie im Header */}
          <div className="flex-1 flex justify-center md:justify-start">
            <CustomLink to="/" className="group inline-block text-center md:text-left">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black uppercase tracking-tighter text-white group-hover:text-[#FDC12F] transition-colors leading-none">
                Namo Catering
              </h2>
              <p className="text-xs md:text-2xl font-bold italic text-[#FDC12F] tracking-wide mt-1">
                ... der mit dem Leopard!
              </p>
            </CustomLink>
          </div>

          {/* Social Media - Rechtsbündig wie die Navigation im Header */}
          <div className="flex-1 flex items-center justify-center md:justify-end">
            <CustomLink to="https://www.instagram.com/namo.catering" newTab className="text-white hover:text-[#FDC12F] transition-all duration-300 hover:scale-110 transform" aria-label="Instagram">
              <Instagram size={36} />
            </CustomLink>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-white/10 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-3 text-white/70 text-sm flex-wrap">
            <p>© 2026 Namo Catering. Alle Rechte vorbehalten.</p>

            <div className="flex items-center gap-4">
              <span className="hidden sm:inline opacity-30">|</span>

              {/* Impressum jetzt als BUTTON mit externer Weiterleitung */}
              <CustomLink to="https://www.namo-catering.de/impressum/" className="text-white font-medium relative group transition-colors duration-300">
                <span>
                  Impressum
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FDC12F] transition-all duration-300 ease-in-out group-hover:w-full" />
                </span>
              </CustomLink>

              <span className="opacity-30">|</span>

              {/* Datenschutzerklärung jetzt als BUTTON mit externer Weiterleitung */}
              <CustomLink to="https://www.namo-catering.de/datenschutzerklaerung/" className="text-white font-medium relative group transition-colors duration-300">
                <span>
                  Datenschutzerklärung
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FDC12F] transition-all duration-300 ease-in-out group-hover:w-full" />
                </span>
              </CustomLink>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}